import type { MailDto, MailSearchDto, MailThreadDetailDto, MailThreadDto } from '@nsb/core';
import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { api, errorText, queryClient, useApi } from '../../api.ts';
import { useFeedbackRecord } from '../../components/feedback-context.ts';
import { BackLink, Button, Confirm, ErrorLine, LoadError, Loading, Page, TextInput, Title } from '../../components/ui.tsx';
import { ProjectCrumb } from './common.tsx';
import { assetsKey, useProject, useProjectId } from './hooks.ts';
import { dateWords } from '../../days.ts';

/**
 * A project's email (Jason, 2026-10-03: "the portal to be the single source of
 * truth"): the threads with its people, kept here as they were sent, replies
 * included, attachments in Files. The portal suggests; NSB says which belong.
 * Gmail is read, never changed. NSB's alone.
 */

const mailKey = (projectId: string) => ['project', projectId, 'mail'];
const when = (iso: string | null) => dateWords(iso, 'date');
const stamp = (iso: string) => dateWords(iso, 'moment');
const names = (t: MailThreadDto) => t.people.map((p) => p.name ?? p.email).join(', ') || 'Only NSB';
const span = (t: MailThreadDto) => (t.firstAt && t.lastAt && when(t.firstAt) !== when(t.lastAt) ? `${when(t.firstAt)} – ${when(t.lastAt)}` : when(t.lastAt));

export function Mail() {
  const projectId = useProjectId();
  const { data: project } = useProject(projectId);
  const { data, error } = useApi<MailDto>(mailKey(projectId), `/projects/${projectId}/mail`);
  const [busy, setBusy] = useState<string | null>(null);
  const [all, setAll] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  useFeedbackRecord(projectId, project ? { type: 'project', id: projectId, label: `${project.name} — email` } : null);
  if (error) return <LoadError error={error} />;
  if (!data || !project) {
    return (
      <Page>
        <Loading />
      </Page>
    );
  }
  const run = async (what: string, path: string) => {
    setBusy(what);
    setErr(null);
    try {
      queryClient.setQueryData(mailKey(projectId), await api<MailDto>(path, { body: {} }));
      // The timeline, the files and the home's needs move with it.
      void queryClient.invalidateQueries({ queryKey: ['project', projectId], predicate: (q) => q.queryKey[2] !== 'mail' });
      void queryClient.invalidateQueries({ queryKey: assetsKey(projectId) });
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setBusy(null);
    }
  };
  return (
    <Page wide>
      <ProjectCrumb project={project} />
      <Title>Email</Title>
      <p className="max-w-2xl text-ink-2">
        Email with this project’s people, kept here as it was sent: every message of the threads that belong, replies included, with their attachments in Files. Only NSB sees it.
        Gmail is read, never changed.
      </p>

      {!data.connected ? (
        <p className="mt-8 max-w-2xl">
          Gmail is not connected.{' '}
          <Link to="/settings/connections" className="link">
            Connect it in Settings
          </Link>
          , with the account where your mail arrives.
        </p>
      ) : (
        <div className="mt-6 flex flex-wrap items-baseline gap-x-6 gap-y-2">
          <Button tone="secondary" busy={busy === 'check'} onClick={() => void run('check', `/projects/${projectId}/mail/check`)}>
            Look now
          </Button>
          <p className="meta">
            {data.people.length
              ? `Looking for threads with ${data.people.map((p) => p.name ?? p.email).join(', ')}, in ${data.account}.`
              : 'This project has no one with an email address to look for: add them in People.'}{' '}
            {data.checkedAt ? (
              <>
                Last looked <span className="num">{stamp(data.checkedAt)}</span>; every ten minutes on its own.
              </>
            ) : null}
          </p>
        </div>
      )}
      <ErrorLine error={err} />

      {data.suggestions.length ? (
        <section className="mt-10">
          <div className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-2 border-b border-ink/40 pb-2">
            <h2 className="text-[1.375rem] sm:text-[1.5rem]">
              To look at <span className="num text-ink-3">{data.suggestions.length}</span>
            </h2>
            <Button tone="quiet" busy={busy === 'all'} onClick={() => setAll(true)}>
              They all belong
            </Button>
          </div>
          {all ? (
            <Confirm
              prompt={`Bring all ${data.suggestions.length} onto the project? Each is kept whole, and kept up to date, from now on.`}
              busy={busy === 'all'}
              onYes={() => void run('all', `/projects/${projectId}/mail/attach-all`).then(() => setAll(false))}
              onNo={() => setAll(false)}
            />
          ) : null}
          <p className="meta mt-2">Threads with the project’s people that are not on it yet. Choose which belong; the rest are not suggested again.</p>
          <ul className="divide-y divide-rule border-b border-rule">
            {data.suggestions.map((t) => (
              <li key={t.id} className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1 py-3">
                <ThreadLine thread={t} />
                <span className="flex items-center gap-x-5 text-sm">
                  <Button tone="quiet" busy={busy === t.id} onClick={() => void run(t.id, `/projects/${projectId}/mail/threads/${t.id}/attach`)}>
                    It belongs
                  </Button>
                  <Button tone="quiet" disabled={busy === t.id} onClick={() => void run(t.id, `/projects/${projectId}/mail/threads/${t.id}/dismiss`)}>
                    Not this project
                  </Button>
                </span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {data.connected ? <Find projectId={projectId} onAdded={(d) => queryClient.setQueryData(mailKey(projectId), d)} /> : null}

      <section className="mt-10">
        <div className="border-b border-ink/40 pb-2">
          <h2 className="text-[1.375rem] sm:text-[1.5rem]">
            On the project <span className="num text-ink-3">{data.threads.length}</span>
          </h2>
        </div>
        {data.threads.length ? (
          <ul className="divide-y divide-rule border-b border-rule">
            {data.threads.map((t) => (
              <li key={t.id}>
                <Link to={`/p/${projectId}/mail/${t.id}`} className="group block py-3">
                  <ThreadLine thread={t} linked />
                </Link>
              </li>
            ))}
          </ul>
        ) : (
          <p className="mt-3 text-ink-3">None yet.</p>
        )}
      </section>
    </Page>
  );
}

function ThreadLine({ thread: t, linked = false }: { thread: MailThreadDto; linked?: boolean }) {
  return (
    <span className="block min-w-0">
      <span className={`block min-w-0 break-words ${linked ? 'group-hover:underline' : ''}`}>{t.subject ?? '(no subject)'}</span>
      <span className="meta block">
        {names(t)} · <span className="num">{t.messageCount}</span> message{t.messageCount === 1 ? '' : 's'} · <span className="num">{span(t)}</span>
      </span>
    </span>
  );
}

/** Any thread, found the way Gmail finds it, onto the project. */
function Find({ projectId, onAdded }: { projectId: string; onAdded: (d: MailDto) => void }) {
  const [q, setQ] = useState('');
  const [found, setFound] = useState<MailSearchDto | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const search = async () => {
    setBusy('search');
    setErr(null);
    try {
      setFound(await api<MailSearchDto>(`/projects/${projectId}/mail/search?q=${encodeURIComponent(q.trim())}`));
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setBusy(null);
    }
  };
  const add = async (gmailThreadId: string) => {
    setBusy(gmailThreadId);
    setErr(null);
    try {
      onAdded(await api<MailDto>(`/projects/${projectId}/mail/found/${gmailThreadId}`, { body: {} }));
      void queryClient.invalidateQueries({ queryKey: ['project', projectId], predicate: (k) => k.queryKey[2] !== 'mail' });
      setFound((f) => (f ? { results: f.results.map((r) => (r.gmailThreadId === gmailThreadId ? { ...r, state: 'on' } : r)) } : f));
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setBusy(null);
    }
  };
  return (
    <section className="mt-10 max-w-3xl">
      <form
        className="flex flex-wrap items-end gap-x-4 gap-y-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (q.trim()) void search();
        }}
      >
        <label className="min-w-0 flex-1">
          <span className="label block">Find a thread the suggestions missed</span>
          <TextInput value={q} onChange={(e) => setQ(e.target.value)} placeholder="Words in it, or from:… subject:…" />
        </label>
        <Button type="submit" tone="secondary" busy={busy === 'search'} disabled={!q.trim()}>
          Search the mailbox
        </Button>
      </form>
      <ErrorLine error={err} />
      {found ? (
        found.results.length ? (
          <ul className="mt-3 divide-y divide-rule border-y border-rule">
            {found.results.map((t) => (
              <li key={t.gmailThreadId} className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1 py-3">
                <ThreadLine thread={t} />
                {t.state === 'on' ? (
                  <span className="label">On the project</span>
                ) : (
                  <Button tone="quiet" busy={busy === t.gmailThreadId} onClick={() => void add(t.gmailThreadId)}>
                    Bring it in
                  </Button>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <p className="meta mt-3">Nothing in the mailbox matches that.</p>
        )
      ) : null}
    </section>
  );
}

/** One thread, message by message, as it was sent. */
export function MailThread() {
  const projectId = useProjectId();
  const threadId = useParams<{ threadId: string }>().threadId!;
  const { data: project } = useProject(projectId);
  const { data, error } = useApi<MailThreadDetailDto>(['project', projectId, 'mail', threadId], `/projects/${projectId}/mail/threads/${threadId}`);
  useFeedbackRecord(projectId, data ? { type: 'mail_thread', id: threadId, label: data.thread.subject ?? 'Email thread' } : null);
  if (error) return <LoadError error={error} />;
  if (!data || !project) {
    return (
      <Page>
        <Loading />
      </Page>
    );
  }
  const t = data.thread;
  return (
    <Page>
      <BackLink to={`/p/${projectId}/mail`} label="Email" />
      <Title kicker={`${t.messageCount} message${t.messageCount === 1 ? '' : 's'} · ${span(t)}`}>{t.subject ?? '(no subject)'}</Title>
      <p className="meta -mt-3 mb-6">
        With {names(t)}
        {t.gmailUrl ? (
          <>
            {' · '}
            <a href={t.gmailUrl} target="_blank" rel="noreferrer" className="link">
              Open in Gmail
            </a>
          </>
        ) : null}
      </p>
      <ol className="divide-y divide-rule border-y border-rule">
        {data.messages.map((m) => (
          <Message key={m.id} projectId={projectId} message={m} />
        ))}
      </ol>
    </Page>
  );
}

function Message({ projectId, message: m }: { projectId: string; message: MailThreadDetailDto['messages'][number] }) {
  const [whole, setWhole] = useState(false);
  const to = [...m.to, ...m.cc].map((p) => p.name ?? p.email).join(', ');
  const quoted = m.text.trim() !== m.fresh.trim();
  return (
    <li className="py-5">
      <div className="flex flex-wrap items-baseline justify-between gap-x-6">
        <p>
          <span className="font-medium">{m.from?.name ?? m.from?.email ?? 'Someone'}</span>
          {m.outbound ? <span className="label ml-2 align-[0.1em]">Sent</span> : null}
        </p>
        <span className="num meta">{stamp(m.sentAt)}</span>
      </div>
      {to ? <p className="meta">To {to}</p> : null}
      <p className="mt-3 max-w-measure whitespace-pre-line break-words">{whole ? m.text : m.fresh || m.text}</p>
      {quoted ? (
        <button type="button" className="link mt-2 min-h-11 text-sm text-ink-2 hover:text-ink" onClick={() => setWhole(!whole)} aria-expanded={whole}>
          {whole ? 'Only this message, without what it quotes' : 'The whole message, with what it quotes'}
        </button>
      ) : null}
      {m.attachments.length ? (
        <p className="mt-2 flex flex-wrap gap-x-4 text-sm">
          {m.attachments.map((a) => (
            <Link key={a.id} to={`/p/${projectId}/reviews/files/${a.id}?from=files`} className="link text-ink-2 hover:text-ink">
              {a.name}
            </Link>
          ))}
        </p>
      ) : null}
    </li>
  );
}
